// src/analytics/events.js
// Business-specific analytics event tracking functions
// These functions wrap trackEvent with domain-specific event names and parameters

import { trackEvent } from './analyticsClient';

// Debug logging helper
const logEventCall = (functionName, params) => {
  if (process.env.REACT_APP_ENVIRONMENT === 'development') {
    console.log(`[Analytics Events] 📤 ${functionName} called with:`, params);
  }
};

/**
 * Tracks when a client is searched by DNI
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {string} params.customerDni - Customer DNI searched
 * @param {boolean} [params.found] - Whether the client was found (optional)
 * @param {boolean} [params.hasHomonyms] - Whether there are homonyms (optional)
 */
export function trackClientSearched(params) {
  const { commerceId, branchId, customerDni, found, hasHomonyms } = params || {};
  
  logEventCall('trackClientSearched', params);
  
  if (!commerceId || !customerDni) {
    console.warn('[Analytics Events] ⚠️ trackClientSearched: commerceId and customerDni are required');
    return;
  }

  trackEvent('client_searched', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    customer_dni: customerDni,
    found: found !== undefined ? found : undefined,
    has_homonyms: hasHomonyms !== undefined ? hasHomonyms : undefined
  });
}

/**
 * Tracks when a new client is created/registered
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {string} params.customerDni - Customer DNI
 * @param {string} [params.customerName] - Customer name (optional)
 * @param {string} [params.customerGender] - Customer gender (optional)
 */
export function trackNewClientCreated(params) {
  const { commerceId, branchId, customerDni, customerName, customerGender } = params || {};
  
  logEventCall('trackNewClientCreated', params);
  
  if (!commerceId || !customerDni) {
    console.warn('[Analytics Events] ⚠️ trackNewClientCreated: commerceId and customerDni are required');
    return;
  }

  trackEvent('new_client_created', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    customer_dni: customerDni,
    customer_name: customerName || undefined,
    customer_gender: customerGender || undefined
  });
}

/**
 * Tracks when a client profile is viewed
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {string} params.customerDni - Customer DNI
 * @param {number} params.availableCredit - Available credit amount
 */
export function trackClientProfileViewed(params) {
  const { commerceId, branchId, customerDni, availableCredit } = params || {};
  
  logEventCall('trackClientProfileViewed', params);
  
  if (!commerceId || !customerDni || availableCredit === undefined) {
    console.warn('[Analytics Events] ⚠️ trackClientProfileViewed: commerceId, customerDni, and availableCredit are required');
    return;
  }

  trackEvent('client_profile_viewed', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    customer_dni: customerDni,
    available_credit: parseFloat(availableCredit)
  });
}

/**
 * Tracks when a POS session is opened (seller logs in and enters requests module)
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {string} [params.channel] - Channel identifier (optional)
 * @param {string} [params.customerDni] - Customer DNI (optional)
 */
export function trackPosSessionOpened(params) {
  const { commerceId, branchId, channel, customerDni } = params || {};
  
  logEventCall('trackPosSessionOpened', params);
  
  if (!commerceId) {
    console.warn('[Analytics Events] ⚠️ trackPosSessionOpened: commerceId is required');
    return;
  }

  trackEvent('pos_session_opened', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    channel: channel || undefined,
    customer_dni: customerDni || undefined
  });
}

/**
 * Tracks when a new request is started (new request request created)
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {number} params.availableCredit - Available credit amount (not request amount)
 * @param {string} [params.currency] - Currency code (default: 'ARS')
 * @param {string} [params.customerDni] - Customer DNI (optional)
 */
export function trackRequestStarted(params) {
  const { commerceId, branchId, availableCredit, currency = 'ARS', customerDni } = params || {};
  
  logEventCall('trackRequestStarted', params);
  
  if (!commerceId || availableCredit === undefined) {
    console.warn('[Analytics Events] ⚠️ trackRequestStarted: commerceId and availableCredit are required');
    return;
  }

  trackEvent('request_started', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    available_credit: parseFloat(availableCredit),
    currency: currency,
    customer_dni: customerDni || undefined
  });
}

/**
 * Tracks when offers are searched/calculated
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {number} params.amount - Amount being searched
 * @param {string} [params.productName] - Product name (optional)
 * @param {string} [params.productCategory] - Product category name (optional)
 * @param {string} [params.customerDni] - Customer DNI (optional)
 */
export function trackOffersSearched(params) {
  const { commerceId, branchId, amount, productName, productCategory, customerDni } = params || {};
  
  logEventCall('trackOffersSearched', params);
  
  if (!commerceId || amount === undefined) {
    console.warn('[Analytics Events] ⚠️ trackOffersSearched: commerceId and amount are required');
    return;
  }

  trackEvent('offers_searched', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    amount: parseFloat(amount),
    product_name: productName || undefined,
    product_category: productCategory || undefined,
    customer_dni: customerDni || undefined
  });
}

/**
 * Tracks when an installment plan is selected
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {number} params.amount - Financed amount
 * @param {number} params.installments - Number of installments
 * @param {number} [params.interestRate] - Interest rate (optional)
 * @param {string} [params.customerDni] - Customer DNI (optional)
 * @param {boolean} [params.isDownpaymentPlan] - Whether this is a downpayment plan (optional)
 * @param {string} [params.downpaymentType] - Type of downpayment: 'percentage' or 'installment' (optional)
 * @param {number} [params.downpaymentValue] - Downpayment value (percentage or number of installments) (optional)
 * @param {number} [params.downpaymentAmount] - Downpayment amount (optional)
 * @param {number} [params.originalAmount] - Original amount before downpayment (optional)
 */
export function trackInstallmentPlanSelected(params) {
  const { 
    commerceId, 
    branchId, 
    amount, 
    installments, 
    interestRate, 
    customerDni,
    isDownpaymentPlan,
    downpaymentType,
    downpaymentValue,
    downpaymentAmount,
    originalAmount
  } = params || {};
  
  logEventCall('trackInstallmentPlanSelected', params);
  
  if (!commerceId || amount === undefined || installments === undefined) {
    console.warn('[Analytics Events] ⚠️ trackInstallmentPlanSelected: commerceId, amount, and installments are required');
    return;
  }

  trackEvent('installment_plan_selected', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    value: parseFloat(amount),
    installments: parseInt(installments),
    interest_rate: interestRate !== undefined ? parseFloat(interestRate) : undefined,
    customer_dni: customerDni || undefined,
    is_downpayment_plan: isDownpaymentPlan !== undefined ? isDownpaymentPlan : undefined,
    downpayment_type: downpaymentType || undefined,
    downpayment_value: downpaymentValue !== undefined ? (downpaymentType === 'percentage' ? parseFloat(downpaymentValue) : parseInt(downpaymentValue)) : undefined,
    downpayment_amount: downpaymentAmount !== undefined ? parseFloat(downpaymentAmount) : undefined,
    original_amount: originalAmount !== undefined ? parseFloat(originalAmount) : undefined
  });
}

/**
 * Tracks when a request is completed (confirmed/approved)
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {number} params.amount - Request amount
 * @param {number} params.installments - Number of installments
 * @param {string} [params.customerRiskCategory] - Customer risk category (optional)
 * @param {string} [params.customerDni] - Customer DNI (optional)
 */
export function trackRequestCompleted(params) {
  const { commerceId, branchId, amount, installments, customerRiskCategory, customerDni } = params || {};
  
  logEventCall('trackRequestCompleted', params);
  
  if (!commerceId || amount === undefined || installments === undefined) {
    console.warn('[Analytics Events] ⚠️ trackRequestCompleted: commerceId, amount, and installments are required');
    return;
  }

  trackEvent('request_completed', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    value: parseFloat(amount),
    installments: parseInt(installments),
    customer_risk_category: customerRiskCategory || undefined,
    customer_dni: customerDni || undefined
  });
}

/**
 * Tracks when a request flow is abandoned
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {string} [params.step] - Step where abandonment occurred (optional)
 * @param {string} [params.reason] - Reason for abandonment (optional)
 * @param {string} [params.customerDni] - Customer DNI (optional)
 */
export function trackRequestAbandoned(params) {
  const { commerceId, branchId, step, reason, customerDni } = params || {};
  
  logEventCall('trackRequestAbandoned', params);
  
  if (!commerceId) {
    console.warn('[Analytics Events] ⚠️ trackRequestAbandoned: commerceId is required');
    return;
  }

  trackEvent('request_abandoned', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    step: step || undefined,
    reason: reason || undefined,
    customer_dni: customerDni || undefined
  });
}

/**
 * Tracks when a collection payment attempt is made
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {number} params.amount - Payment amount
 * @param {number} [params.installmentsPaid] - Number of installments being paid (optional)
 * @param {boolean} [params.isPartial] - Whether this is a partial payment (optional)
 */
export function trackCollectionPaymentAttempt(params) {
  const { commerceId, branchId, amount, installmentsPaid, isPartial } = params || {};
  
  logEventCall('trackCollectionPaymentAttempt', params);
  
  if (!commerceId || amount === undefined) {
    console.warn('[Analytics Events] ⚠️ trackCollectionPaymentAttempt: commerceId and amount are required');
    return;
  }

  trackEvent('collection_payment_attempt', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    value: parseFloat(amount),
    installments_paid: installmentsPaid !== undefined ? parseInt(installmentsPaid) : undefined,
    is_partial: isPartial !== undefined ? isPartial : undefined
  });
}

/**
 * Tracks when a collection payment is successful
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {number} params.amount - Payment amount
 * @param {number} [params.installmentsPaid] - Number of installments paid (optional)
 * @param {string} [params.paymentMethod] - Payment method used (optional)
 */
export function trackCollectionPaymentSuccess(params) {
  const { commerceId, branchId, amount, installmentsPaid, paymentMethod } = params || {};
  
  logEventCall('trackCollectionPaymentSuccess', params);
  
  if (!commerceId || amount === undefined) {
    console.warn('[Analytics Events] ⚠️ trackCollectionPaymentSuccess: commerceId and amount are required');
    return;
  }

  trackEvent('collection_payment_success', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    value: parseFloat(amount),
    installments_paid: installmentsPaid !== undefined ? parseInt(installmentsPaid) : undefined,
    payment_method: paymentMethod || undefined
  });
}

/**
 * Tracks when a collection payment fails
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {number} params.amount - Payment amount attempted
 * @param {string} [params.errorCode] - Error code (optional)
 * @param {string} [params.errorMessage] - Error message (optional)
 */
export function trackCollectionPaymentFailed(params) {
  const { commerceId, branchId, amount, errorCode, errorMessage } = params || {};
  
  logEventCall('trackCollectionPaymentFailed', params);
  
  if (!commerceId || amount === undefined) {
    console.warn('[Analytics Events] ⚠️ trackCollectionPaymentFailed: commerceId and amount are required');
    return;
  }

  trackEvent('collection_payment_failed', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    value: parseFloat(amount),
    error_code: errorCode || undefined,
    error_message: errorMessage || undefined
  });
}

// ============================================================================
// STEP-BY-STEP TRACKING EVENTS (POS Flow)
// ============================================================================

/**
 * Tracks when user enters a specific step in the request flow
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {string} params.stepName - Name of the step (e.g., 'DataEntryStep', 'PaymentStep')
 * @param {string} [params.customerDni] - Customer DNI (optional)
 */
export function trackRequestStepEntered(params) {
  const { commerceId, branchId, stepName, customerDni } = params || {};
  
  logEventCall('trackRequestStepEntered', params);
  
  if (!commerceId || !stepName) {
    console.warn('[Analytics Events] ⚠️ trackRequestStepEntered: commerceId and stepName are required');
    return;
  }

  trackEvent('request_step_entered', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    step_name: stepName,
    customer_dni: customerDni || undefined
  });
}

/**
 * Tracks when user completes a specific step in the request flow
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {string} params.stepName - Name of the step (e.g., 'DataEntryStep', 'PaymentStep')
 * @param {string} [params.customerDni] - Customer DNI (optional)
 */
export function trackRequestStepCompleted(params) {
  const { commerceId, branchId, stepName, customerDni } = params || {};
  
  logEventCall('trackRequestStepCompleted', params);
  
  if (!commerceId || !stepName) {
    console.warn('[Analytics Events] ⚠️ trackRequestStepCompleted: commerceId and stepName are required');
    return;
  }

  trackEvent('request_step_completed', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    step_name: stepName,
    customer_dni: customerDni || undefined
  });
}

// ============================================================================
// MOBILE CUSTOMER FLOW EVENTS
// ============================================================================

/**
 * Tracks when customer flow starts on mobile device
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {string} [params.customerDni] - Customer DNI (optional)
 * @param {string} [params.flowId] - Flow ID (optional)
 */
export function trackMobileCustomerFlowStarted(params) {
  const { commerceId, branchId, customerDni, flowId } = params || {};
  
  logEventCall('trackMobileCustomerFlowStarted', params);
  
  if (!commerceId) {
    console.warn('[Analytics Events] ⚠️ trackMobileCustomerFlowStarted: commerceId is required');
    return;
  }

  trackEvent('mobile_customer_flow_started', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    customer_dni: customerDni || undefined,
    flow_id: flowId || undefined
  });
}

/**
 * Tracks when customer enters a step in mobile flow
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {string} params.stepName - Name of the step (e.g., 'ValidaStep', 'PaymentStep', 'ConfirmationStep')
 * @param {string} [params.customerDni] - Customer DNI (optional)
 * @param {string} [params.flowId] - Flow ID (optional)
 */
export function trackMobileCustomerFlowStepEntered(params) {
  const { commerceId, branchId, stepName, customerDni, flowId } = params || {};
  
  logEventCall('trackMobileCustomerFlowStepEntered', params);
  
  if (!commerceId || !stepName) {
    console.warn('[Analytics Events] ⚠️ trackMobileCustomerFlowStepEntered: commerceId and stepName are required');
    return;
  }

  trackEvent('mobile_customer_flow_step_entered', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    step_name: stepName,
    customer_dni: customerDni || undefined,
    flow_id: flowId || undefined
  });
}

/**
 * Tracks when customer completes a step in mobile flow
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {string} params.stepName - Name of the step (e.g., 'ValidaStep', 'PaymentStep', 'ConfirmationStep')
 * @param {string} [params.customerDni] - Customer DNI (optional)
 * @param {string} [params.flowId] - Flow ID (optional)
 */
export function trackMobileCustomerFlowStepCompleted(params) {
  const { commerceId, branchId, stepName, customerDni, flowId } = params || {};
  
  logEventCall('trackMobileCustomerFlowStepCompleted', params);
  
  if (!commerceId || !stepName) {
    console.warn('[Analytics Events] ⚠️ trackMobileCustomerFlowStepCompleted: commerceId and stepName are required');
    return;
  }

  trackEvent('mobile_customer_flow_step_completed', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    step_name: stepName,
    customer_dni: customerDni || undefined,
    flow_id: flowId || undefined
  });
}

/**
 * Tracks when customer completes identity validation step
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {string} [params.customerDni] - Customer DNI (optional)
 * @param {string} [params.flowId] - Flow ID (optional)
 */
export function trackMobileIdentityValidationCompleted(params) {
  const { commerceId, branchId, customerDni, flowId } = params || {};
  
  logEventCall('trackMobileIdentityValidationCompleted', params);
  
  if (!commerceId) {
    console.warn('[Analytics Events] ⚠️ trackMobileIdentityValidationCompleted: commerceId is required');
    return;
  }

  trackEvent('mobile_identity_validation_completed', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    customer_dni: customerDni || undefined,
    flow_id: flowId || undefined
  });
}

/**
 * Tracks when customer saves payment card
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {string} [params.customerDni] - Customer DNI (optional)
 * @param {string} [params.flowId] - Flow ID (optional)
 * @param {string} [params.cardBrand] - Card brand (optional)
 */
export function trackMobilePaymentCardSaved(params) {
  const { commerceId, branchId, customerDni, flowId, cardBrand } = params || {};
  
  logEventCall('trackMobilePaymentCardSaved', params);
  
  if (!commerceId) {
    console.warn('[Analytics Events] ⚠️ trackMobilePaymentCardSaved: commerceId is required');
    return;
  }

  trackEvent('mobile_payment_card_saved', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    customer_dni: customerDni || undefined,
    flow_id: flowId || undefined,
    card_brand: cardBrand || undefined
  });
}

/**
 * Tracks when customer confirms request in mobile flow
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {string} [params.customerDni] - Customer DNI (optional)
 * @param {string} [params.flowId] - Flow ID (optional)
 */
export function trackMobileRequestConfirmed(params) {
  const { commerceId, branchId, customerDni, flowId } = params || {};
  
  logEventCall('trackMobileRequestConfirmed', params);
  
  if (!commerceId) {
    console.warn('[Analytics Events] ⚠️ trackMobileRequestConfirmed: commerceId is required');
    return;
  }

  trackEvent('mobile_request_confirmed', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    customer_dni: customerDni || undefined,
    flow_id: flowId || undefined
  });
}

/**
 * Tracks when customer flow is completed on mobile
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {string} [params.customerDni] - Customer DNI (optional)
 * @param {string} [params.flowId] - Flow ID (optional)
 */
export function trackMobileCustomerFlowCompleted(params) {
  const { commerceId, branchId, customerDni, flowId } = params || {};
  
  logEventCall('trackMobileCustomerFlowCompleted', params);
  
  if (!commerceId) {
    console.warn('[Analytics Events] ⚠️ trackMobileCustomerFlowCompleted: commerceId is required');
    return;
  }

  trackEvent('mobile_customer_flow_completed', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    customer_dni: customerDni || undefined,
    flow_id: flowId || undefined
  });
}

/**
 * Tracks when customer enters document upload step on mobile
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {string} [params.customerDni] - Customer DNI (optional)
 * @param {string} [params.flowId] - Flow ID (optional)
 */
export function trackMobileDocumentUploadStepEntered(params) {
  const { commerceId, branchId, customerDni, flowId } = params || {};
  
  logEventCall('trackMobileDocumentUploadStepEntered', params);
  
  if (!commerceId) {
    console.warn('[Analytics Events] ⚠️ trackMobileDocumentUploadStepEntered: commerceId is required');
    return;
  }

  trackEvent('mobile_document_upload_step_entered', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    customer_dni: customerDni || undefined,
    flow_id: flowId || undefined
  });
}

/**
 * Tracks when a document is uploaded on mobile
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {string} params.documentCategory - Document category (e.g., 'documentFront', 'documentBack', 'selfie')
 * @param {string} [params.customerDni] - Customer DNI (optional)
 * @param {string} [params.flowId] - Flow ID (optional)
 */
export function trackMobileDocumentUploaded(params) {
  const { commerceId, branchId, documentCategory, customerDni, flowId } = params || {};
  
  logEventCall('trackMobileDocumentUploaded', params);
  
  if (!commerceId || !documentCategory) {
    console.warn('[Analytics Events] ⚠️ trackMobileDocumentUploaded: commerceId and documentCategory are required');
    return;
  }

  trackEvent('mobile_document_uploaded', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    document_category: documentCategory,
    customer_dni: customerDni || undefined,
    flow_id: flowId || undefined
  });
}

/**
 * Tracks when a document is uploaded in POS flow
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {string} params.documentCategory - Document category (e.g., 'documentFront', 'documentBack', 'selfie')
 * @param {string} [params.customerDni] - Customer DNI (optional)
 * @param {boolean} [params.uploadedFromMobile] - Whether document was uploaded from mobile (optional)
 */
export function trackDocumentUploaded(params) {
  const { commerceId, branchId, documentCategory, customerDni, uploadedFromMobile } = params || {};
  
  logEventCall('trackDocumentUploaded', params);
  
  if (!commerceId || !documentCategory) {
    console.warn('[Analytics Events] ⚠️ trackDocumentUploaded: commerceId and documentCategory are required');
    return;
  }

  trackEvent('document_uploaded', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    document_category: documentCategory,
    customer_dni: customerDni || undefined,
    uploaded_from_mobile: uploadedFromMobile !== undefined ? uploadedFromMobile : undefined
  });
}

/**
 * Tracks when authorization modal is opened
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {string} [params.customerDni] - Customer DNI (optional)
 * @param {number} [params.requestedAmount] - Amount being requested (optional)
 * @param {string} [params.reason] - Reason for opening modal (e.g., 'amount_exceeds_limit', 'manual_request') (optional)
 */
export function trackAuthorizationModalOpened(params) {
  const { commerceId, branchId, customerDni, requestedAmount, reason } = params || {};
  
  logEventCall('trackAuthorizationModalOpened', params);
  
  if (!commerceId) {
    console.warn('[Analytics Events] ⚠️ trackAuthorizationModalOpened: commerceId is required');
    return;
  }

  trackEvent('authorization_modal_opened', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    customer_dni: customerDni || undefined,
    requested_amount: requestedAmount !== undefined ? parseFloat(requestedAmount) : undefined,
    reason: reason || undefined
  });
}

/**
 * Tracks when an authorization request is submitted
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {string} [params.customerDni] - Customer DNI (optional)
 * @param {number} params.amount - Amount requested
 * @param {boolean} [params.hasAttachment] - Whether an attachment was included (optional)
 * @param {boolean} [params.requiresPassword] - Whether password was required (optional)
 * @param {string} [params.authorizationStatus] - Status of authorization ('approved', 'pending') (optional)
 */
export function trackAuthorizationRequested(params) {
  const { commerceId, branchId, customerDni, amount, hasAttachment, requiresPassword, authorizationStatus } = params || {};
  
  logEventCall('trackAuthorizationRequested', params);
  
  if (!commerceId || amount === undefined) {
    console.warn('[Analytics Events] ⚠️ trackAuthorizationRequested: commerceId and amount are required');
    return;
  }

  trackEvent('authorization_requested', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    customer_dni: customerDni || undefined,
    amount: parseFloat(amount),
    has_attachment: hasAttachment !== undefined ? hasAttachment : undefined,
    requires_password: requiresPassword !== undefined ? requiresPassword : undefined,
    authorization_status: authorizationStatus || undefined
  });
}

/**
 * Tracks when an authorization request fails
 * @param {Object} params - Event parameters
 * @param {string} params.commerceId - Commerce/Channel ID
 * @param {string} [params.branchId] - Branch ID (optional)
 * @param {string} [params.customerDni] - Customer DNI (optional)
 * @param {number} params.amount - Amount that was requested
 * @param {string} [params.errorCode] - Error code (optional)
 * @param {string} [params.errorMessage] - Error message (optional)
 */
export function trackAuthorizationRequestFailed(params) {
  const { commerceId, branchId, customerDni, amount, errorCode, errorMessage } = params || {};
  
  logEventCall('trackAuthorizationRequestFailed', params);
  
  if (!commerceId || amount === undefined) {
    console.warn('[Analytics Events] ⚠️ trackAuthorizationRequestFailed: commerceId and amount are required');
    return;
  }

  trackEvent('authorization_request_failed', {
    commerce_id: commerceId,
    branch_id: branchId || undefined,
    customer_dni: customerDni || undefined,
    amount: parseFloat(amount),
    error_code: errorCode || undefined,
    error_message: errorMessage || undefined
  });
}

